/* eslint-disable import/prefer-default-export */
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/restrict-template-expressions, @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars, @typescript-eslint/no-floating-promises, @typescript-eslint/no-non-null-assertion */

/* ------------------------ Functional imports start ------------------------ */
import Cookies from 'js-cookie'
import i18next from 'i18next'
import { jwtDecode } from 'jwt-decode'
import { type Readable } from 'stream'

import {
  type Address,
  type Contact,
  type FileInfo,
  type GuestUser,
  type OrganizationUser,
  type RichContact,
  type Role,
  type User,
  type UserPreferences,
} from './types'

import { createCustomer } from '../features/Customer/utils/functions'
import { type Customer } from '../features/Customer/utils/types'
import { type FormFieldType } from '../features/Form'
import { createArticle } from '../features/Item/utils/functions'
import {
  type CustomerOrder,
  type ProductionOrder,
  type SupplyOrder,
} from '../features/ProductionOrder/utils/types'
import { createRequest } from '../features/Request/utils/functions'
import { type NewPermissions } from '../features/Roles/utils/types'
import { createSupplier } from '../features/Supplier/utils/functions'
import { type Supplier } from '../features/Supplier/utils/types'
import { createGuestRequest } from '../features/UnauthRequest/utils/functions'
import LogTool from '../logger/logTools'
import { APIAddressFromJSON } from '../generated-types/models/APIAddress'
import { APIContactFromJSON } from '../generated-types/models/APIContact'
import {
  APICustomerOrderFromJSON,
  type APICustomerOrder,
} from '../generated-types/models/APICustomerOrder'
import {
  type APIProductionOrder,
  APIProductionOrderFromJSON,
} from '../generated-types/models/APIProductionOrder'
import { type APIRole, APIRoleFromJSON } from '../generated-types/models/APIRole'
import {
  type APISupplyOrder,
  APISupplyOrderFromJSON,
} from '../generated-types/models/APISupplyOrder'
import {
  type APIAllInOneUser,
  APIAllInOneUserFromJSON,
} from '../generated-types/models/APIAllInOneUser'
import { APIRichContactFromJSON } from '../generated-types/models/APIRichContact'
import { createContract } from '../features/Contract/api/contractApi'
import { FetchKwargsSingle } from '../api/types'
/* ------------------------- Functional imports end ------------------------- */

const log = new LogTool({ context: 'generalUtils', enable: false, logLevel: 'debug' })

/* -------------------------------------------------------------------------- */
/*                           Utility functions start                          */
/* -------------------------------------------------------------------------- */
/**
 * Date Formatting Utilities
 *
 * Provides locale-aware date formatting for display
 */

/**
 * Format a date string for display based on locale
 * @param dateString ISO date string (YYYY-MM-DD or full ISO datetime)
 * @param locale Optional locale string (defaults to browser locale)
 * @returns Formatted date string according to locale
 */
export function formatDateForDisplay(dateString: string, locale?: string): string {
  if (!dateString) return ''

  try {
    const date = new Date(dateString)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString
    }

    // Use Intl.DateTimeFormat for locale-aware formatting
    const formatter = new Intl.DateTimeFormat(resolveLocale(locale), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })

    return formatter.format(date)
  } catch (error) {
    // If formatting fails, return original string
    return dateString
  }
}

/**
 * Resolve the locale to use for date formatting.
 *
 * Priority:
 *  1. Explicitly passed `locale` parameter
 *  2. Current i18next application language (set via language settings)
 *  3. Browser language (`navigator.language`)
 *  4. Hard-coded fallback `'de-DE'` (for SSR / Node environments)
 */
function resolveLocale(locale?: string): string {
  if (locale) return locale

  // Use the language the user selected in the app settings (i18next)
  const i18nLang = i18next.language // e.g. 'de', 'en', 'es', 'fr'
  if (i18nLang) return i18nLang

  // Fall back to browser language
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language

  return 'de-DE'
}

/**
 * Render a date/time value for display in tables and UI components.
 * Uses `Intl.DateTimeFormat` for locale-aware formatting so that e.g.
 *   - `de` / `de-DE` renders `23.02.2026` or `23.02.2026, 14:30`
 *   - `en` / `en-US` renders `02/23/2026` or `02/23/2026, 2:30 PM`
 *
 * The locale is resolved automatically from the current i18next language
 * (set via the platform language settings) unless overridden explicitly.
 *
 * @param date     The date value to format (ISO string, Date object, epoch ms, or nullish).
 * @param options  Optional configuration object.
 * @param options.showTime  When `true` the formatted string includes hours and minutes. Defaults to `false`.
 * @param options.locale    BCP-47 locale tag (e.g. `'de-DE'`). Overrides the automatic detection.
 * @param options.fallback  String returned when `date` is falsy or invalid. Defaults to `'-'`.
 * @returns The formatted date (and optionally time) string, or the fallback value.
 */
export function renderDateTime(
  date: string | Date | number | null | undefined,
  options?: {
    showTime?: boolean
    locale?: string
    fallback?: string
  }
): string {
  const { showTime = false, locale, fallback = '-' } = options ?? {}

  if (date === null || date === undefined || date === '') return fallback

  try {
    const dateObj = date instanceof Date ? date : new Date(date)

    if (isNaN(dateObj.getTime())) return fallback

    const formatOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }

    if (showTime) {
      formatOptions.hour = '2-digit'
      formatOptions.minute = '2-digit'
    }

    return new Intl.DateTimeFormat(resolveLocale(locale), formatOptions).format(dateObj)
  } catch {
    return fallback
  }
}

/**
 * Gets roles from the user and builds the permissions object
 * @param roles from current user
 * @returns permissions object for the current user
 */
export const rolesToPermissions = (roles: Role[]): NewPermissions => {
  const rolesPermissions: any[] = roles.map(role => (role.permissions ?? {}) as any)

  // Merge permissions per resource instead of overriding
  const merged: any = rolesPermissions.reduce<any>((acc, curr) => {
    for (const [resource, actions] of Object.entries(curr)) {
      acc[resource] = { ...(acc[resource] ?? {}), ...(actions as any) }
    }
    return acc
  }, {})

  const permissions: NewPermissions = {}
  for (const [resource, actions] of Object.entries(merged)) {
    permissions[resource] = Object.keys(actions as any)
  }
  return permissions
}
/**
 * Check if the user has the permission to view a certain API endpoint
 * @param permissions the permissions of the user
 * @param apiPermissionType the API endpoint to check
 */
export function canView(permissions: NewPermissions, apiPermissionType: string): boolean {
  return permissions[apiPermissionType]?.includes('view')
}

/**
 * Check if the user has the permission to edit a certain API endpoint
 * @param permissions the permissions of the user
 * @param apiPermissionType the API endpoint to check
 */
export function canEdit(permissions: NewPermissions, apiPermissionType: string): boolean {
  return permissions[apiPermissionType]?.includes('edit')
}

/**
 * Check if the user has the permission to delete a certain API endpoint
 * @param permissions the permissions of the user
 * @param apiPermissionType the API endpoint to check
 */
export function canDelete(permissions: NewPermissions, apiPermissionType: string): boolean {
  return permissions[apiPermissionType]?.includes('delete')
}

/**
 * Check if the user has the permission to create a certain API endpoint
 * @param permissions the permissions of the user
 * @param apiPermissionType the API endpoint to check
 */
export function canCreate(permissions: NewPermissions, apiPermissionType: string): boolean {
  return permissions[apiPermissionType]?.includes('create')
}

/**
 * Check if the user is privileged by being an org admin, superuser or staff
 * @param user the user to check
 * @param type the type of privilege to check for
 */
export function isPrivileged(
  user: User | undefined,
  type: 'STAFF' | 'ORGADMIN' | 'SUPERUSER' = 'ORGADMIN'
): boolean {
  switch (type) {
    case 'STAFF':
      return user?.isStaff || false
    case 'ORGADMIN':
      return user?.isOrgAdmin || false
    default:
      return user?.isStaff || user?.isOrgAdmin || user?.isSuperuser || false
  }
}

export const ALPHANUMERIC = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
/**
 * Generates a random string of a specific length.
 * @param {number} length - The desired string length.
 * @param {string} chars - A string containing all allowed characters.
 * @returns {string}
 */
export function getRandomString(length: number, chars = ALPHANUMERIC): string {
  let result = ''
  const charactersLength = chars.length
  for (let i = 0; i < length; i++) {
    // Pick a random index based on the chars length
    const randomIndex = Math.floor(Math.random() * charactersLength)
    result += chars.charAt(randomIndex)
  }
  return result
}

/**
 * Filtering utility function with correct typing
 * Returns true if the value is not falsy e.g. not null or undefined
 * @param value to be checked
 * @returns boolean whether the value is not falsy
 */
export const notFalsy = <T>(value: T): value is NonNullable<typeof value> => !!value

/**
 * Formats given bytes into human-readable sizes
 * @param bytes number of bytes
 * @param decimals number of decimals displayed
 * */
export function formatBytes(bytes: number, decimals = 2): string {
  if (!bytes) return ''
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)).toString() + ' ' + sizes[i]
}

/**
 * Downloads file from a given Blob Object and a filename
 * @param blob object to download
 * @param filename filename to save as
 * */
export function downloadBlob(
  blob: Blob | Readable | ReadableStream<unknown> | undefined,
  filename: string
): HTMLAnchorElement {
  const url = URL.createObjectURL(blob as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || 'download'
  const clickHandler = (): void => {
    setTimeout(() => {
      URL.revokeObjectURL(url)
      a.removeEventListener('click', clickHandler)
    }, 150)
  }
  a.addEventListener('click', clickHandler, false)
  a.click()
  return a
}

/**
 * Parsing function for nested objects within server responses. E.g. the supplier of an article.
 * @param nestedResponseObject The contents of a field that might contain a nested object.
 * @param createObject The function that should be used to parse the nested response object to a frontend object.
 * @returns The frontend object created by the createObject function or a backend hyperlink string pointing to the object.
 */
export function parseNestedObject<T>(
  nestedResponseObject: unknown,
  createObject: (nestedObject: unknown) => T
): T | string {
  if (typeof nestedResponseObject === 'object' && nestedResponseObject !== null) {
    return createObject(nestedResponseObject)
  }
  // the response object does not contain an actual object. It probably contains the backend hyperlink pointing to that object
  return nestedResponseObject as string | T
}

export function parseNestedObjectList<T>(
  fieldResponse: unknown,
  createObject: (nestedObject: unknown) => T
): T[] | string {
  if (Array.isArray(fieldResponse)) {
    if (fieldResponse.every(field => typeof field === 'object' && field !== null)) {
      // fieldResponse is a list of objects
      return fieldResponse.map(field => createObject(field))
    }
  }
  // fieldResponse does not contain objects
  // response likely contains url that leads to objects
  return fieldResponse as T[] | string
}

export function getStringPreview(string: string, previewLength = 50): string {
  let preview = string.trimStart().substring(0, previewLength).trimEnd()
  if (string.trim().length > previewLength) {
    // add ellipses to indicate that the message is longer than what the preview displays
    preview += ' ...'
  }
  return preview
}

export function createAddress(addressResponse: unknown): Address {
  const apiAddress = APIAddressFromJSON(addressResponse)
  const extras =
    typeof addressResponse === 'object' && addressResponse !== null
      ? (addressResponse as Record<string, unknown>)
      : {}
  return {
    ...apiAddress,
    ...extras,
    key: getPKfromSelf(apiAddress.self),
  }
}

/**
 * Returns a representative string for an address.
 * @param address The address to generate the string representation for.
 * @param includeCity Wether or not the address representation should contain the addresses city information.
 * @param includeCountry Wether or not the address representation should contain the addresses country information.
 */
export function getAddressRepresentation(
  address: Address,
  includeCity = true,
  includeCountry = true
): string {
  let repr = `${address.address1}`
  if (address.address2) {
    repr += `, ${address.address2}`
  }
  if (address.address3) {
    repr += `, ${address.address3}`
  }
  if (includeCity && address.city) {
    if (address.zipCode) {
      repr += `, ${address.zipCode} ${address.city}`
    } else {
      repr += `, ${address.city}`
    }
  }
  if (includeCountry && address.country) {
    repr += `, ${address.country}`
  }
  return repr
}

export function createContact(contactResponse: unknown): Contact {
  const apiContact = APIContactFromJSON(contactResponse)
  return {
    ...apiContact,
    key: getPKfromSelf(apiContact.self),
    addresses: parseNestedObjectList(apiContact.addresses, createAddress),
  }
}

export function createRichContact(richContactResponse: unknown): RichContact {
  const apiRichContact = APIRichContactFromJSON(richContactResponse)
  return {
    ...apiRichContact,
    key: getPKfromSelf(apiRichContact.self),
    addresses: parseNestedObjectList(apiRichContact.addresses, createAddress),
    suppliers: parseNestedObjectList(
      apiRichContact.suppliers,
      createSupplier
    ) as unknown as Supplier[],
    customers: parseNestedObjectList(
      apiRichContact.customers,
      createCustomer
    ) as unknown as Customer[],
  }
}

/**
 * Returns a representative string for a contact.
 * @param contact
 * @param contactFor The name of a supplier / customer that the contact is linked with.
 * @returns
 */
export function getContactRepresentation(contact: Contact, contactFor?: string): string {
  let representation = ''
  if (contact.firstName && contact.lastName) {
    representation += `${contact.firstName} ${contact.lastName}`
  } else {
    representation += `${contact.email}`
  }
  if (contactFor) {
    representation += ` (${contactFor})`
  }

  return representation
}

export function createRole(roleResponse: unknown): Role {
  const apiRole: APIRole = APIRoleFromJSON(roleResponse)

  return {
    ...apiRole,
    key: getPKfromSelf(apiRole.self),
  }
}

export function createUserPreferences(preferencesResponse: unknown): UserPreferences {
  if (!preferencesResponse || typeof preferencesResponse !== 'object') {
    return {
      language: '',
    }
  }
  const prefs = preferencesResponse as Record<string, unknown>
  return {
    language: (prefs.language as string) ?? '',
  }
}

export function createUser(userResponse: unknown): User {
  const apiAllInOneUser: APIAllInOneUser = APIAllInOneUserFromJSON(userResponse)
  log.debug('APIAllInOneUser ->', apiAllInOneUser)
  if (apiAllInOneUser.isGuestUser) {
    return {
      ...apiAllInOneUser,
      key: getPKfromSelf(apiAllInOneUser.self),
      email: `guest-${apiAllInOneUser.id.substring(4, 9)}`,
      firstName: 'Guest',
      lastName: 'User',
      // sharepoint: parseNestedObject(apiAllInOneUser.sharepoint, createSharepoint),
      repFor: parseNestedObject(apiAllInOneUser.repFor, createContact),
      roles: parseNestedObjectList(apiAllInOneUser.roles, createRole),
      // objectPermissions: apiAllInOneUser.objectPermissions || {},
      preferences: createUserPreferences(apiAllInOneUser.preferences),
    }
  }
  return {
    ...apiAllInOneUser,
    key: getPKfromSelf(apiAllInOneUser.self),
    roles: parseNestedObjectList(apiAllInOneUser.roles, createRole),
    // objectPermissions: apiAllInOneUser.objectPermissions || {},
    preferences: createUserPreferences(apiAllInOneUser.preferences),
  }
}

export function createGuestUser(guestUserResponse: unknown): GuestUser {
  log.debug('GuestUserInput ->', guestUserResponse)
  return createUser(guestUserResponse) as GuestUser
}

export function getUserRepresentation(user: User): string {
  log.debug('userInput ->', user)
  if (user.isGuestUser) {
    return getGuestUserRepresentation(user as GuestUser)
  } else {
    return getOrgUserRepresentation(user as OrganizationUser)
  }
}

export function getOrgUserRepresentation(orgUser: OrganizationUser): string {
  let representation = ''
  if (orgUser.firstName && orgUser.lastName) {
    representation += `${orgUser.firstName} ${orgUser.lastName}`
  } else {
    representation += orgUser.email
  }
  return representation
}

export function getGuestUserRepresentation(guestUser: GuestUser): string {
  let representation = ''
  log.debug(
    'get representation for ->',
    guestUser,
    ', repFor === Object ->',
    typeof guestUser.repFor === 'object'
  )
  if (typeof guestUser.repFor === 'object') {
    // the guest user represents a contact
    representation += getContactRepresentation(guestUser.repFor)
  } else {
    representation += guestUser.comment
  }
  if (representation === '') {
    // The guest user neither represents a contact nor provides a comment that identifies its purpose.
    // This should never happen, but just in case we provide a fallback representation.
    representation += 'Unidentified Guest'
  }
  return representation
}

export function createFileInfo(fileResponse: unknown): FileInfo {
  const file = fileResponse as Record<string, unknown>

  const result: Record<string, unknown> = {}

  // typescript File properties
  if (file.lastModified) result.lastModified = file.lastModified as number
  if (file.webkitRelativePath) result.webkitRelativePath = file.webkitRelativePath as string
  if (file.size) result.size = file.size as number
  if (file.type) result.type = file.type as string

  // general backend file properties
  if (file.self) result.self = file.self as string
  if (file.document) result.document = file.document as string
  if (file.name) result.name = file.name as string
  // Prefer stored_at, but also handle created_at/createdAt to support guest request files
  if (file.stored_at) result.storedAt = new Date(file.stored_at as string)
  else if (file.created_at) result.storedAt = new Date(file.created_at as string)
  else if (file.createdAt) result.storedAt = new Date(file.createdAt as string)
  if (file.last_modified_at) result.lastModifiedAt = new Date(file.last_modified_at as string)
  else if (file.lastModifiedAt) result.lastModifiedAt = new Date(file.lastModifiedAt as string)

  // articleFile properties
  if (file.article) {
    result.article = parseNestedObject(file.article, createArticle)
  }

  // contractFile properties
  if (file.contract) {
    result.contract = parseNestedObject(file.contract, value => createContract(value as any))
  }

  // requestFile properties
  if (file.request) {
    result.request = parseNestedObject(file.request, createRequest)
  }

  // guestRequest properties
  if (file.guest_request) {
    result.guestRequest = parseNestedObject(file.guest_request, createGuestRequest)
  }

  return result as FileInfo
}

export function formInputToAPIAddressJSON(addressInput: unknown): Record<string, unknown> {
  const input = addressInput as Record<string, unknown>
  const result: Record<string, unknown> = {}

  // if the input provides a certain property, then add it as a JSON key-value pair
  if (input.address1) result.address_1 = input.address1
  if (input.address2) result.address_2 = input.address2
  if (input.address3) result.address_3 = input.address3
  if (input.zipCode) result.zip_code = input.zipCode
  if (input.city) result.city = input.city
  if (input.country) result.country = input.country
  if (input.type) result.type = input.type
  if (input.info) result.info = input.info

  return result
}

/**
 * Converts a string written in camelCase to snake_case.
 * Note: This function only works properly for camelCase input strings! Results
 *  might be unexpected for other cases.
 * @param str
 * @returns
 */
export function toSnakeCase(str: string) {
  return str
    .replace(/[A-Z]/g, char => `_${char.toLowerCase()}`)
    .replace(/[0-9]/g, char => `_${char}`)
}

/**
 * Append additional parameters to a url used for an API-Request.
 * @param url The 'base-url' to append the parameters to.
 * @param paramKey
 * @param paramValues
 * @returns
 */
export function appendURLParameter(url: string, paramKey: string, paramValues: string): string {
  let resultUrl: string = url

  // check if the url contains a ? already
  if (resultUrl.includes('?')) {
    // we are not appending the first parameter to the url -> use the & delimiter
    resultUrl += '&'
  } else {
    resultUrl += '?'
  }

  // append the parameter key and values
  resultUrl += paramKey + '=' + paramValues
  return resultUrl
}

export function buildFetchUrl(baseUrl: string, parameter: string[]): string {
  let fetchUrl = baseUrl
  for (const param of parameter) {
    // syntax check
    if (!param.match(/(\w+)=(\w+)(,\w+)*/))
      throw new Error(`Syntax error in URL parameter ${param}`)

    // seperate the parameter key and values
    const [paramKey, paramValues] = param.split('=')

    if (baseUrl.includes(paramKey)) {
      // the parameter already exists in the baseUrl
      // -> do not append the parameter and continue with the next parameter
      continue
    } else {
      fetchUrl = appendURLParameter(fetchUrl, paramKey, paramValues)
    }
  }

  return fetchUrl
}

/**
 * Retrieve object identifier from url string.
 * @param self django self hyperlink url
 * @returns backend object id of the object that is fetchable through the self-url
 */
export function getPKfromSelf(self: string): string {
  if (!self) return ''
  // get all strings enclosed by slashes that contain at least one digit
  const pkMatches = self.match(/[^/]*\d[^/]*/g)
  if (pkMatches !== null) {
    // the last match is the object identifier we are looking for
    log.debug('matches ->', pkMatches)
    return pkMatches[pkMatches.length - 1]
  }
  throw new Error('The provided string does not contain a backend object identifier.')
}

/**
 * Check if any required form field is not filled out yet.
 * @param formFields The form fields at hand.
 * @param formInput The state that holds the form field input data.
 * @returns true if any required form field is not filled out yet.
 */
export function missingRequiredFormFields(
  formFields: FormFieldType[],
  formInput: unknown
): boolean {
  const input = formInput as Record<string, unknown>
  return formFields.some((field: FormFieldType) => {
    // current field is not required -> all good
    if (!field.required) return false
    // the field is required but the form state has no data (for that field)
    if (!input || (input && !input[field.key])) return true
    if (input[field.key]) {
      // the form state holds data for the current form field, but ...
      if (typeof input[field.key] === 'string') {
        // ... it might be just spaces
        return (input[field.key] as string).trim() === ''
      }
      // check for object data as provided by e.g. measurement fields
      if (typeof input[field.key] === 'object') {
        return Object.values(input[field.key] as Record<string, unknown>).some(
          (value: unknown) =>
            typeof value === 'undefined' || (value as { trim?: unknown }).trim === ''
        )
      }
    }
    return false
  })
}

export function createProductionOrder(productionOrderResponse: unknown): ProductionOrder {
  const apiProductionOrder: APIProductionOrder = APIProductionOrderFromJSON(productionOrderResponse)
  return {
    ...apiProductionOrder,
    key: getPKfromSelf(apiProductionOrder.self),
  }
}

export function createSupplyOrder(supplyOrderResponse: unknown): SupplyOrder {
  const apiSupplyOrder: APISupplyOrder = APISupplyOrderFromJSON(supplyOrderResponse)
  return {
    ...apiSupplyOrder,
    key: getPKfromSelf(apiSupplyOrder.self),
    article: apiSupplyOrder.article,
    supplier: apiSupplyOrder.supplier,
  }
}

export function createCustomerOrder(customerOrderResponse: unknown): CustomerOrder {
  const apiCustomerOrder: APICustomerOrder = APICustomerOrderFromJSON(customerOrderResponse)
  return {
    ...apiCustomerOrder,
    key: getPKfromSelf(apiCustomerOrder.self),
    article: apiCustomerOrder.article,
    customer: apiCustomerOrder.customer,
  }
}

/**
 * Convert Request form input to API JSON
 */
function extractEditorSelf(candidate: any, visited = new Set<any>()): string | null {
  if (!candidate) {
    return null
  }
  if (typeof candidate === 'string') {
    return candidate
  }
  if (typeof candidate !== 'object') {
    return null
  }
  if (visited.has(candidate)) {
    return null
  }
  visited.add(candidate)

  const directKeys = ['self', 'url', 'value', 'href']
  for (const key of directKeys) {
    const value = candidate[key]
    if (typeof value === 'string') {
      return value
    }
  }

  const nestedKeys = ['value', 'user', 'option', 'data']
  for (const key of nestedKeys) {
    const nested = candidate[key]
    if (nested && typeof nested === 'object') {
      const resolved = extractEditorSelf(nested, visited)
      if (resolved) {
        return resolved
      }
    }
  }

  return null
}

export function normalizeEditors(editors: any): string[] | undefined {
  if (!Array.isArray(editors)) {
    return undefined
  }
  const normalized = editors
    .map(editor => extractEditorSelf(editor))
    .filter((self): self is string => Boolean(self))
  return normalized
}

/* -------------------------------------------------------------------------- */
/*                             API functions start                            */
/* -------------------------------------------------------------------------- */

export async function copyToClipboard(value: string | number): Promise<'SUCCESS' | 'ERROR'> {
  try {
    await navigator.clipboard.writeText(String(value))
  } catch (err) {
    log.error('Error while copying to clipboard', err)
    return 'ERROR'
  }
  log.info('Success copying value', value, ' to clipboard.')
  return 'SUCCESS'
}

/**
 * Refreshes the access token if it is expired using the refresh token
 */
export async function refreshAccessToken(): Promise<string | undefined> {
  const refreshToken = Cookies.get('refresh')
  if (!refreshToken) return undefined
  const envApiUrl: string | undefined = process.env.REACT_APP_API_URL
  if (!envApiUrl) throw new Error('API Url is not defined')
  return fetch(envApiUrl + 'v1/auth/token/refresh/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh: refreshToken }),
  })
    .then(res => {
      if (!res.ok) {
        // redirect to login page
        /* window.location.href = '/login' */
        throw new Error(res.statusText)
      } else {
        return res.json()
      }
    })
    .then(res => {
      const decoded = jwtDecode(res.access)
      Cookies.set('access', res.access, { expires: new Date((decoded.exp as number) * 1000) })
      return res.access
    })
}

/**
 * Fetch an object from AWS S3 storage.
 * @param onSuccess The url where the S3 object lives.
 * @param url s3 object url
 * @param onSuccess Callback that is executed upon receiving a response from AWS S3.
 * @param onError Callback that is executed upon receiving an error response.
 */
export async function fetchS3Object({
  url,
  onSuccess,
  onError = (error: unknown) => null,
}: FetchKwargsSingle<Blob> & { url: string }): Promise<void> {
  log.info('Begin fetching S3 object for URL ->', url)

  const response = await fetch(url)
  if (response.ok) {
    log.info('Success fetching S3 object')
    const blob = await response.blob()
    onSuccess(blob)
  } else {
    // store the response.text in a variable, as it can only be consumed once!
    const responseText = response.text()
    log.error('Error fetching S3 object', responseText)
    onError(responseText)
  }
}

/**
 *
 * @param url
 * @param fileName
 * @param MIMEType
 */
export function awsPresignedUrlDownload(
  url: string,
  fileName = '',
  MIMEType = ''
): HTMLAnchorElement {
  const downloadAnchor = document.createElement('a')
  downloadAnchor.setAttribute('href', url)
  downloadAnchor.setAttribute('type', MIMEType)
  downloadAnchor.setAttribute('download', fileName)
  document.body.appendChild(downloadAnchor)
  downloadAnchor.click()
  downloadAnchor.remove()
  return downloadAnchor
}

/**
 * @deprecated Use fetchArticles from src/features/Item/api/articleApi.ts instead
 */
export const loadCountries = async (): Promise<
  { value: string; label?: string; disabled?: boolean }[]
> => {
  try {
    const response = await fetch('https://restcountries.com/v3.1/all?fields=name')
    if (!response.ok) {
      log.error('Error fetching countries', response)
      return []
    }
    const countries = await response.json()
    return countries
      .map((country: unknown) => {
        const c = country as Record<string, unknown>
        const name = c.name as Record<string, unknown>
        return {
          value: name.common,
          label: name.common,
        }
      })
      .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label))
  } catch (error) {
    log.error('Error fetching countries', error)
    return []
  }
}
